import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, MapPin, MessageCircle, Zap, Microscope, HeartHandshake } from "lucide-react";
import { BlogLayout } from "@/pages/blog/BlogLayout";
import { useSeo } from "@/pages/blog/useSeo";
import { MaskLine, Reveal } from "@/components/landing/Reveal";
import { WHATSAPP_URL, BOOK_PERIO_PATH, ADDRESS, CENTER } from "@/components/landing/data";
import { MAPS_DIRECTIONS } from "@/components/landing/Location";
import { TOWNS, townBySlug } from "@/content/towns";

const POINTS = [
  { icon: Zap, title: "Laser a tre lunghezze d'onda", text: "450, 635 e 808 nm per decontaminare le tasche gengivali, ridurre l'infiammazione e favorire la guarigione." },
  { icon: Microscope, title: "Microscopio operatorio", text: "Il tartaro sotto gengiva si rimuove con precisione, senza bisturi e senza punti." },
  { icon: HeartHandshake, title: "Pensato per chi ha paura", text: "Prima si parla, poi si decide. Un segnale per fermarsi, sempre, e su indicazione la sedazione cosciente." },
];

const jsonLd = (t) => ({
  "@context": "https://schema.org",
  "@type": ["MedicalClinic", "Dentist"],
  name: "Centro di Parodontologia — Dott. Nicola Mauro",
  url: `https://centrodiparodontologia.com/parodontite/${t.slug}`,
  medicalSpecialty: "Periodontics",
  telephone: "+39 393 518 9869",
  address: { "@type": "PostalAddress", streetAddress: ADDRESS.street, postalCode: ADDRESS.cap, addressLocality: ADDRESS.city, addressRegion: ADDRESS.prov, addressCountry: "IT" },
  areaServed: t.name,
});

export default function TownPage() {
  const t = townBySlug(useParams().town);
  useSeo({
    title: t ? `Parodontite a ${t.name}: cura con il laser, senza paura` : "Parodontite",
    description: t ? `Cura della parodontite per chi vive a ${t.name}: laser, microscopio operatorio e un percorso per chi ha paura del dentista. Dott. Nicola Mauro, ${CENTER}, Via della Pace 41, Galliera (BO).` : "",
    path: `/parodontite/${t?.slug ?? ""}`,
    jsonLd: t ? jsonLd(t) : undefined,
  });
  if (!t) return <Navigate to="/" replace />;
  return (
    <BlogLayout>
      <div data-testid="town-page">
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-36 sm:pt-44 pb-20">
          <p className="eyebrow text-[#3E6B48]">Parodontologo vicino a {t.name}</p>
          <h1 className="f-serif text-5xl sm:text-7xl text-[#1B3B28] mt-5 leading-[0.98] max-w-4xl" data-testid="town-title">
            <MaskLine>Parodontite a {t.name}?</MaskLine>
            <MaskLine delay={0.12}><em className="text-[#3E6B48]">Si cura, con calma.</em></MaskLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="text-lg text-[#526356] mt-8 max-w-2xl leading-relaxed" data-testid="town-intro">{t.intro}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={BOOK_PERIO_PATH} data-testid="town-cta-booking" className="inline-flex items-center gap-2 rounded-full bg-[#1B3B28] text-[#FCFBF7] px-6 py-3.5 text-sm hover:bg-[#3E6B48] transition-colors">Prenota la prima visita <ArrowUpRight size={15} /></Link>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="town-cta-whatsapp" className="inline-flex items-center gap-2 rounded-full border border-[#1B3B28]/20 text-[#1B3B28] px-6 py-3.5 text-sm hover:bg-[#E6ECE4] transition-colors"><MessageCircle size={15} /> WhatsApp</a>
            </div>
          </Reveal>
        </section>
        <section className="bg-[#0F2317] py-24">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid gap-5 md:grid-cols-3">
            {POINTS.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-[#FCFBF7]/10 p-8">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-[#86C89B] text-[#0F2317]"><Icon size={18} /></span>
                  <h2 className="f-serif text-3xl text-[#FCFBF7] mt-6 leading-tight">{title}</h2>
                  <p className="text-[#FCFBF7]/70 mt-3 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="f-serif text-3xl sm:text-4xl text-[#1B3B28] leading-snug">{t.note}</p>
            <p className="mt-6 text-[#526356] leading-relaxed">Il percorso: prima visita con sondaggio parodontale, terapia laser-assistita, controllo a 10-15 giorni, rivalutazione a 2 mesi e mantenimento. Non sai se le tue gengive sono a rischio? Prova il <Link to="/test-gengive" className="underline underline-offset-4 text-[#1B3B28]" data-testid="town-quiz-link">test di 1 minuto</Link>.</p>
          </Reveal>
          <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.15}>
            <div className="rounded-3xl bg-[#E6ECE4] p-7">
              <p className="eyebrow text-[#3E6B48] flex items-center gap-2"><MapPin size={13} /> Dove siamo</p>
              <p className="f-serif text-2xl text-[#1B3B28] mt-3">{CENTER}</p>
              <p className="text-[#526356] mt-1">{ADDRESS.street}, {ADDRESS.cap} {ADDRESS.city} ({ADDRESS.prov})</p>
              <p className="text-[#526356] mt-1 text-sm">Dal martedì al venerdì, 9:00–18:00</p>
              <a href={MAPS_DIRECTIONS} target="_blank" rel="noopener noreferrer" data-testid="town-directions" className="mt-5 inline-flex items-center gap-2 text-sm text-[#1B3B28] underline underline-offset-4">Indicazioni stradali da {t.name} <ArrowUpRight size={14} /></a>
            </div>
          </Reveal>
        </section>
        <section className="max-w-[1400px] mx-auto px-6 sm:px-10 pb-24" data-testid="town-links">
          <p className="eyebrow text-[#526356]">Ci scelgono anche da</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {TOWNS.filter((x) => x.slug !== t.slug).map((x) => (
              <Link key={x.slug} to={`/parodontite/${x.slug}`} data-testid={`town-link-${x.slug}`} className="rounded-full border border-[#1B3B28]/15 px-4 py-2 text-sm text-[#1B3B28] hover:bg-[#E6ECE4] transition-colors">{x.name}</Link>
            ))}
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
